import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #desktopMediaQuery = window.matchMedia('(min-width: 990px)');

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#desktopMediaQuery.addEventListener('change', this.#onBreakpointChange);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#desktopMediaQuery.removeEventListener('change', this.#onBreakpointChange);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.close();
  };

  /** Reset nested navigation state when its responsive interaction model changes. */
  #onBreakpointChange = () => {
    this.querySelectorAll('.menu-drawer__menu-container--nested').forEach((container) => {
      if (!(container instanceof HTMLElement)) return;

      if (container.contains(document.activeElement)) {
        const trigger = container.querySelector(':scope > .menu-drawer__accordion-button');
        if (trigger instanceof HTMLButtonElement) trigger.focus();
      }

      reset(container);
    });

    if (this.isOpen) trapFocus(this.refs.details);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest drawer navigation container to the event target
   * @param {Event | undefined} event
   * @returns {HTMLElement}
   */
  #getMenuContainer(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('.menu-drawer__menu-container--nested, details') ?? this.refs.details;
  }

  /**
   * Close any open sibling top-level submenu(s) next to the given container.
   * Without this, tapping directly from one open top-level submenu (e.g. "Kūno ir
   * plaukų priežiūra") straight to another top-level item (e.g. "Produktai") left the
   * previous submenu's open/focus-trap state active, so the new submenu appeared not
   * to open until the user first backed out or re-toggled the original item.
   * @param {HTMLElement} details
   */
  #closeOpenSiblings(details) {
    if (!details.classList.contains('menu-drawer__menu-container--nested')) return;
    const parent = details.parentElement;
    if (!parent) return;

    parent.querySelectorAll(':scope > .menu-drawer__menu-container--nested.menu-open').forEach((sibling) => {
      if (sibling !== details && sibling instanceof HTMLElement) {
        this.#close(sibling);
      }
    });
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getMenuContainer(event);
    const summary = details.querySelector(':scope > summary, :scope > .menu-drawer__accordion-button');

    if (!(summary instanceof HTMLElement)) return;

    // Ensure switching directly between top-level items always shows the newly
    // selected submenu, even if another one was already open.
    this.#closeOpenSiblings(details);

    summary.setAttribute('aria-expanded', 'true');
    const controlledPanel = getControlledPanel(details, summary);
    controlledPanel?.setAttribute('aria-hidden', 'false');
    controlledPanel?.removeAttribute('inert');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getMenuContainer(event));
  }

  /**
   * Open a mobile third-level screen or toggle its desktop accordion.
   * @param {Event} event
   */
  toggleNestedMenu(event) {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('.menu-drawer__accordion-button');
    if (!(button instanceof HTMLButtonElement)) return;

    if (!this.#desktopMediaQuery.matches) {
      this.open('submenu', event);
      return;
    }

    const shouldOpen = button.getAttribute('aria-expanded') !== 'true';
    const list = button.closest('.menu-drawer__menu--childlist');

    list?.querySelectorAll('.menu-drawer__accordion-button[aria-expanded="true"]').forEach((openButton) => {
      if (openButton instanceof HTMLButtonElement && openButton !== button) {
        this.#setAccordionState(openButton, false);
      }
    });

    this.#setAccordionState(button, shouldOpen);
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLElement} details
   */
  #close(details) {
    const summary = details.querySelector(':scope > summary, :scope > .menu-drawer__accordion-button');

    if (!(summary instanceof HTMLElement)) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    if (details === this.refs.details) {
      this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        details.querySelectorAll('.menu-drawer__menu-container--nested').forEach(reset);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          const hasOpenSubmenu = this.refs.menuDrawer.querySelector('details[open]');
          this.refs.menuDrawer.classList.toggle('menu-drawer--has-submenu-opened', Boolean(hasOpenSubmenu));
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Synchronize a desktop accordion trigger with its controlled content.
   * @param {HTMLButtonElement} button
   * @param {boolean} expanded
   */
  #setAccordionState(button, expanded) {
    const panelId = button.getAttribute('aria-controls');
    const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;

    button.setAttribute('aria-expanded', String(expanded));
    button.closest('.menu-drawer__menu-container--nested')?.classList.toggle('is-open', expanded);

    if (panel instanceof HTMLElement) {
      panel.setAttribute('aria-hidden', String(!expanded));
      panel.toggleAttribute('inert', !expanded);
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open navigation container to its original state
 *
 * @param {HTMLElement} element
 */
function reset(element) {
  element.classList.remove('menu-open', 'is-open');
  if (element instanceof HTMLDetailsElement) element.removeAttribute('open');

  const trigger = element.querySelector(':scope > summary, :scope > .menu-drawer__accordion-button');
  trigger?.setAttribute('aria-expanded', 'false');

  const controlledPanel = getControlledPanel(element, trigger);
  controlledPanel?.setAttribute('aria-hidden', 'true');
  controlledPanel?.setAttribute('inert', '');
}

/**
 * @param {HTMLElement} container
 * @param {Element | null} trigger
 * @returns {HTMLElement | null}
 */
function getControlledPanel(container, trigger) {
  const panelId = trigger?.getAttribute('aria-controls');
  const panel = panelId ? container.querySelector(`#${CSS.escape(panelId)}`) : null;

  return panel instanceof HTMLElement ? panel : null;
}